import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["input", "addInput", "addButton", "list", "rowTemplate"];

  connect() {
    this.itemId = 0;
    this.openRow = null;
    this.pointerDownRow = null;
    this.handleDocumentPointerDown = this.handleDocumentPointerDown.bind(this);
    this.closeOpenItemOnDocumentClick = this.closeOpenItemOnDocumentClick.bind(this);
    this.handleRowFocusIn = this.handleRowFocusIn.bind(this);
    this.handleRowFocusOut = this.handleRowFocusOut.bind(this);
    document.addEventListener("pointerdown", this.handleDocumentPointerDown);
    document.addEventListener("click", this.closeOpenItemOnDocumentClick);

    this.element.closest("form")?.addEventListener("submit", () => this.updateInput());
    this.updateAddButtonState();
    this.updateInput();
  }

  disconnect() {
    document.removeEventListener("pointerdown", this.handleDocumentPointerDown);
    document.removeEventListener("click", this.closeOpenItemOnDocumentClick);

    if (this.openRow) {
      this.openRow.removeEventListener("focusout", this.handleRowFocusOut);
    }
  }

  addOnEnter(event) {
    if (event.key !== "Enter") return;

    event.preventDefault();
    this.addItem();
  }

  addItem() {
    const name = this.addInputTarget.value.trim();
    if (name === "") return;

    const row = this.rowTemplateTarget.content.firstElementChild.cloneNode(true);
    const rowId = ++this.itemId;
    const nameInput = row.querySelector("[data-role='name']");

    row.dataset.name = name;
    nameInput.id = `document_request_requested_file_name_${rowId}`;
    nameInput.value = name;

    row.querySelectorAll("input, textarea").forEach(input => {
      input.addEventListener("input", () => this.updateSummary(row));
      input.addEventListener("change", () => this.updateSummary(row));
    });
    row.addEventListener("focusin", this.handleRowFocusIn);
    this.updateSummary(row);
    row.classList.add("is-entering");
    row.style.height = "0px";
    this.listTarget.appendChild(row);

    requestAnimationFrame(() => {
      row.style.height = `${row.scrollHeight}px`;
      row.classList.add("is-visible");
      this.listTarget.scrollTo({ top: this.listTarget.scrollHeight, behavior: "smooth" });
    });

    row.addEventListener("transitionend", event => {
      if (event.propertyName === "height") {
        row.classList.remove("is-entering", "is-visible");
        row.style.height = "";
        this.addInputTarget.focus();
        this.addInputTarget.closest(".multi-input-add").scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
    }, { once: true });

    this.addInputTarget.value = "";
    this.updateAddButtonState();
    this.updateInput();
  }

  updateInput() {
    const values = Array.from(this.listTarget.querySelectorAll(".row"))
      .map(row => ({
        name: row.dataset.name || "-",
        description: row.querySelector("[data-role='description']").value.trim(),
        optional: row.querySelector("[data-role='optional']").checked
      }))
    
    this.inputTarget.value = JSON.stringify(values);
  }

  updateAddButtonState() {
    this.addButtonTarget.disabled = this.addInputTarget.value.trim() === "";
  }

  toggleItem(event) {
    const button = event.currentTarget;
    const row = button.closest(".document-request-row");
    const panel = row.querySelector("[data-role='panel']");
    const isOpen = button.getAttribute("aria-expanded") === "true";
    const clickedSummaryButton = event.target.closest(".document-request-row-summary-button");

    if (isOpen) {
      if (clickedSummaryButton) {
        this.closePanel(button, panel);
        return;
      }

      panel.querySelector("[data-role='name']").focus();
      return;
    }

    this.closeOtherOpenItems(row);
    this.openPanel(button, panel);
  }

  openPanel(button, panel, { focusField = true } = {}) {
    const row = button.closest(".document-request-row");

    if (this.openRow && this.openRow !== row) {
      this.openRow.removeEventListener("focusout", this.handleRowFocusOut);
    }

    this.openRow = row;
    this.openRow.addEventListener("focusout", this.handleRowFocusOut);
    button.setAttribute("aria-expanded", "true");
    panel.hidden = false;
    panel.style.height = "0px";
    panel.style.opacity = "0";

    requestAnimationFrame(() => {
      panel.style.height = `${panel.scrollHeight}px`;
      panel.style.opacity = "1";

      if (focusField) {
        panel.querySelector("[data-role='name']").focus();
      }
    });

    panel.addEventListener("transitionend", () => {
      if (button.getAttribute("aria-expanded") === "true") {
        panel.style.height = "auto";
      }
    }, { once: true });
  }

  closePanel(button, panel) {
    if (button.getAttribute("aria-expanded") === "false") return;

    const row = button.closest(".document-request-row");
    if (this.openRow === row) {
      this.openRow.removeEventListener("focusout", this.handleRowFocusOut);
      this.openRow = null;
    }

    button.setAttribute("aria-expanded", "false");
    panel.style.height = `${panel.scrollHeight}px`;
    panel.style.opacity = "1";

    requestAnimationFrame(() => {
      panel.style.height = "0px";
      panel.style.opacity = "0";
    });

    panel.addEventListener("transitionend", () => {
      if (button.getAttribute("aria-expanded") === "false") {
        panel.hidden = true;
        panel.style.height = "";
        panel.style.opacity = "";
      }
    }, { once: true });
  }

  closeOpenItemOnDocumentClick(event) {
    if (!this.openRow) return;

    const clickedRow = event.target.closest(".document-request-row");
    if (clickedRow) {
      setTimeout(() => {
        if (this.pointerDownRow === clickedRow) {
          this.pointerDownRow = null;
        }
      }, 0);
      return;
    }

    this.pointerDownRow = null;

    const button = this.openRow.querySelector(".document-request-row-summary-button");
    const panel = this.openRow.querySelector("[data-role='panel']");
    this.closePanel(button, panel);
  }

  handleDocumentPointerDown(event) {
    this.pointerDownRow = event.target.closest(".document-request-row");
  }

  handleRowFocusIn(event) {
    const row = event.currentTarget;
    if (this.openRow === row) return;

    const button = row.querySelector(".document-request-row-summary-button");
    const panel = row.querySelector("[data-role='panel']");
    if (button.getAttribute("aria-expanded") === "true") return;

    this.closeOtherOpenItems(row);
    this.openPanel(button, panel, { focusField: false });
  }

  handleRowFocusOut(event) {
    const row = event.currentTarget;

    requestAnimationFrame(() => {
      const nextFocusedElement = event.relatedTarget || document.activeElement;
      if (row.contains(nextFocusedElement)) return;
      if (this.pointerDownRow === row) return;
      if (this.pointerDownRow && this.pointerDownRow !== row) return;

      const button = row.querySelector(".document-request-row-summary-button");
      const panel = row.querySelector("[data-role='panel']");
      this.closePanel(button, panel);
    });
  }

  closeOtherOpenItems(currentRow) {
    this.listTarget.querySelectorAll(".document-request-row").forEach(row => {
      if (row === currentRow) return;

      const button = row.querySelector(".document-request-row-summary-button");
      if (button.getAttribute("aria-expanded") !== "true") return;

      const panel = row.querySelector("[data-role='panel']");
      this.closePanel(button, panel);
    });
  }

  updateSummary(row) {
    const nameInput = row.querySelector("[data-role='name']");
    const title = row.querySelector("[data-role='summary-title']");
    const description = row.querySelector("[data-role='summary-description']");
    const optional = row.querySelector("[data-role='summary-optional']");
    const name = nameInput.value.trim();
    const descriptionText = row.querySelector("[data-role='description']").value.trim();
    const optionalChecked = row.querySelector("[data-role='optional']").checked;

    row.dataset.name = name;
    nameInput.setAttribute("aria-invalid", name === "" ? "true" : "false");
    title.textContent = name || this.element.dataset.emptyLabel;
    description.textContent = descriptionText;
    optional.textContent = optionalChecked ? `(${this.element.dataset.optionalLabel})` : "";
    this.updateInput();
  }

  removeItemFromButton(event) {
    const row = event.currentTarget.closest(".document-request-row");
    this.removeItem(row);
  }

  removeItem(row) {
    if (this.openRow === row) {
      this.openRow.removeEventListener("focusout", this.handleRowFocusOut);
      this.openRow = null;
    }

    if (this.pointerDownRow === row) {
      this.pointerDownRow = null;
    }

    row.removeEventListener("focusin", this.handleRowFocusIn);

    row.style.height = `${row.scrollHeight}px`;
    row.classList.add("is-removing");

    requestAnimationFrame(() => {
      this.addInputTarget?.focus();
      row.style.height = "0px";
    });

    const handleRemoveTransitionEnd = event => {
      if (event.propertyName !== "height") return;

      row.removeEventListener("transitionend", handleRemoveTransitionEnd);
      row.remove();
      this.updateInput();
    };

    row.addEventListener("transitionend", handleRemoveTransitionEnd);
  }
}
